import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { firstValueFrom, interval, Observable, of, timeout } from 'rxjs';
import { PDFDocument } from 'pdf-lib';

@Injectable({
  providedIn: 'root',
})
export class LabelService {
  constructor(private httpClient: HttpClient) { }

  printShipments(queryParams: any): Observable<Blob> {
    return this.httpClient.get(`${environment.apiUrl}/shipments/print?${queryParams.toString()}`, { responseType: 'blob' });
  }

  async getMultipleLabelsV2(shipmentIds: string[]): Promise<Uint8Array> {
    if (typeof (shipmentIds) != typeof (new Array<string>())) {
      var shipmentId = shipmentIds;
      shipmentIds = new Array<string>();
      shipmentIds.push(shipmentId as any);
    }

    const rawResults = await Promise.all(
      shipmentIds.map(x => this.getLabels(x))
    );
    const mergedPdf = await PDFDocument.create();

    for (const raw of rawResults) {
      const bytes = await this.toPdfBytes(raw);
      if (!bytes || !bytes.length) {
        continue;
      }

      const donor = await PDFDocument.load(bytes);
      const pages = await mergedPdf.copyPages(donor, donor.getPageIndices());
      pages.forEach(p => mergedPdf.addPage(p));
    }

    return await mergedPdf.save();
  }

  async fallbackShipmentServiceGetLabels(id: string) {
    return await this.httpClient.get('/api/e/shipments/labels/shipment/' + id).toPromise()
  }

  async getMultipleLabels(shipmentIds: string[] | string, fromFallBack: boolean = false) {
    let labelUrls;
    if (fromFallBack)
      labelUrls = this.fallbackShipmentServiceGetLabels(shipmentIds as string);
    else
      labelUrls = await this.getMultipleLabelsV2(shipmentIds as string[])
    return await this.mergeAllPDFsToBytes(labelUrls)
  }

  async getSinglePdfFromUrl(url: string): Promise<string | null> {
    if (!url) return null;
    const bytes = await fetch(url).then(r => r.arrayBuffer());
    const pdf = await PDFDocument.load(bytes);
    return await pdf.saveAsBase64({ dataUri: true });
  }

  private async mergeAllPDFs(urls) {
    if (!urls) {
      return null;
    }

    const pdfDoc = await PDFDocument.create();
    const numDocs = urls.length;

    for (var i = 0; i < numDocs; i++) {
      const donorPdfBytes = await fetch(urls[i]).then((res) => res.arrayBuffer());
      const donorPdfDoc = await PDFDocument.load(donorPdfBytes);
      const docLength = donorPdfDoc.getPageCount();
      for (var k = 0; k < docLength; k++) {
        const [donorPage] = await pdfDoc.copyPages(donorPdfDoc, [k]);
        pdfDoc.addPage(donorPage);
      }
    }

    const pdfDataUri = await pdfDoc.saveAsBase64({ dataUri: true });

    return pdfDataUri;
  }
  private async mergeAllPDFsToBytes(temp: Array<string | { bytes: string }>): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.create();
    let base64Pdfs = await temp;

    for (const item of base64Pdfs ?? []) {
      const raw = typeof item === 'string' ? item : item?.bytes ?? '';
      if (!raw) continue;
      if (raw.length <= 1) {
        const pure = this.stripPdfPrefix(base64Pdfs.toString());
        const donor = await PDFDocument.load(this.base64ToBytes(pure));
        const pages = await pdfDoc.copyPages(donor, donor.getPageIndices());
        pages.forEach(p => pdfDoc.addPage(p));
        return await pdfDoc.save();
      } else {

        const pure = this.stripPdfPrefix(raw);
        const donor = await PDFDocument.load(this.base64ToBytes(pure));
        const pages = await pdfDoc.copyPages(donor, donor.getPageIndices());
        pages.forEach(p => pdfDoc.addPage(p));
      }
    }

    return await pdfDoc.save();
  }

  private base64ToBytes(b64: string): Uint8Array {
    b64 = b64.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64);
    const len = bin.length;
    const out = new Uint8Array(len);
    for (let i = 0; i < len; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  private stripPdfPrefix(b64: string): string {
    return (b64 || '').replace(/^data:application\/pdf;base64,/i, '');
  }

  /**
  * 
  * @param shipmentObject can contains a object where object.id is the shipmentId or its only the shipmentId
  * @param tenantId can be null, defualts to ""
  * @returns a uint8Array (bytes array)
  */
  async getLabels(shipmentObject: string | any, tenantId: string = "") {
    if (shipmentObject == undefined) {
      console.error("GetLabels reviced empty or null request shipmentId, shipmentObject = ", shipmentObject)
      return null;
    }

    let shipemntId = "";
    if (typeof shipmentObject === typeof "")
      shipemntId = shipmentObject;
    else if (typeof (shipmentObject.id) === typeof (""))
      shipemntId = shipmentObject.id
    if (shipemntId == "")
      console.error("what the hell went wrong?", shipemntId)

    // incase of failured in labelService, then this "fallback" is used.
    const fallback = async () => {
      console.info("using fallback for some odd reson.", shipemntId)
      return await this.getMultipleLabels([shipemntId], true);
    };

    try {
      // the issuse here is that sometimes labelService gets label from old ob, then it reviced a url, while other times it returns a base64 that the frontend can work with.     
      var res: any = await firstValueFrom(this.httpClient.post(`/api/labels/getlabelbyshipmentids`, { ShipmentId: shipemntId, UseAzureStorageBlob: false }));

      // a small sanity check if all else fails the this servie calls the old ob as a backup on the fallback backup.
      if (res.pdfs && res.pdfs.includes('‼')) {
        return await this.getMultipleLabels([shipmentObject]);
      } else {
        if ((res.pdfs as string).includes('https')) {

          var tempBlob = await firstValueFrom(this.httpClient.get(res.pdfs, { responseType: 'blob' }));
          return new Uint8Array(await tempBlob.arrayBuffer());

        }
        return this.mergeAllPDFsToBytes(res.pdfs)
      }
    } catch {
      return await fallback();
    }

  }

  /**
   * this function is a small helper, whice aim to normilize the way pdfs are handle.
   * @param src returned data from lebelService or shipmentService
   */
  private async toPdfBytes(src: any): Promise<Uint8Array | null> {
    if (!src) return null;

    // Already Uint8Array
    if (src instanceof Uint8Array) {
      return src;
    }

    // ArrayBuffer
    if (src instanceof ArrayBuffer) {
      return new Uint8Array(src);
    }

    // Blob (just in case some other path returns blob)
    if (src instanceof Blob) {
      const buf = await src.arrayBuffer();
      return new Uint8Array(buf);
    }

    // { bytes: string }
    if (src && typeof src === 'object' && typeof src.bytes === 'string') {
      const pure = this.stripPdfPrefix(src.bytes);
      return this.base64ToBytes(pure);
    }

    // string => base64 or data:uri
    if (typeof src === 'string') {
      const pure = this.stripPdfPrefix(src);
      if (!pure) return null;
      return this.base64ToBytes(pure);
    }
    console.warn('Unsupported PDF type from getLabels:', src);
    return null;
  }


  async PrintWayBillsMerged(shipmentIds: string[], ispod: boolean = false): Promise<Uint8Array> {
    const ids = (shipmentIds ?? []).filter(Boolean);
    const merged = await PDFDocument.create();
    for (const id of ids) {
      let res: any;
      if (ispod) {
        res = await firstValueFrom(
          this.httpClient.post(`/api/labels/getwaybillprint`, { shipmentId: id, useFallBack: true, namedTemplate: "pod" } as WayBillQuery)
        );
      } else {
        res = await firstValueFrom(
          this.httpClient.post(`/api/labels/getwaybillprint`, { shipmentId: id, useFallBack: true } as WayBillQuery)
        );
      }
      const bytes = await this.toPdfBytesFromWayBillResponse(res);
      if (!bytes?.length) continue;

      const donor = await PDFDocument.load(bytes);
      const pages = await merged.copyPages(donor, donor.getPageIndices());
      pages.forEach(p => merged.addPage(p));
    }

    return await merged.save();
  }

  private async toPdfBytesFromWayBillResponse(res: any): Promise<Uint8Array | null> {
    if (!res) return null;
    const raw =
      res?.pdfs?.[0]?.bytes ??
      res?.pdfs?.[0] ??
      res?.bytes ??
      res?.pdfs;

    return await this.toPdfBytes(raw);
  }

  PrintWayBill(shipmentIds: any[]) {
    if (shipmentIds[0] != null)
      return this.httpClient.post(`/api/labels/getwaybillprint`, { shipmentId: shipmentIds[0], useFallBack: true } as WayBillQuery);
    else
      return null;
  }

  PrintPackageListFromLabelService(shipmentIds: Array<string>) {
    return this.httpClient.post('/api/labels/printsortinglist', { ShipmentId: shipmentIds });
  }


}




//#region LabelRequest

interface LabelServiceRequest {
  TenantId: string;
  ShipmentId: string;
  TemplateVersion: string;
  Fields: LabelFields;
  ReturnInlinePdf: boolean;
  Collis: SimpleColliLabel[];
}

interface LabelFields {
  Date: string;
  WaybillNo: string;
  TrackingNo: string;
  Service: string;
  ColliDescription: string;
  Sender: AddressBlock;
  Receiver: AddressBlock;
  Remarks: string;
  CustomerNo: string;
  TerminalNo: string;
  Barcode: string;
  colliTotalCount: string;
  CurrentColliNo: string;
  LabelTitle: string;
}

interface AddressBlock {
  Name: string;
  Street: string;
  Street2: string;
  ZipCode: string;
  City: string;
  Country: string;
}

interface SimpleColliLabel {
  Barcode: string;
  Weight: string;
  Type: string;
  Description: string;
}



//#endregion

//#region WayBillRequest

export interface WayBillQuery {
  header: WayBillHeaders;
  receiverAddrese: AddreseBlock;
  senderAddrese: AddreseBlock;
  collis: ColliLines[];
  tenantId?: string | null;      // Guid?
  shipmentId: string;            // Guid
  templateVersion: string;       // default "v1" in C#
  serviceInfo: Service;
  totalsInfo: Totals;
  omexInfo?: Omex | null;
  palletExchangeInfo?: PalletExchange | null;
  namedTemplate: string;
  miscElements: Record<string, string[]>; // Dictionary<string, List<string>>
  fromShipmentService: any;          // MessageContracts.V1.Shared.shipment
  shipmentScans?: TrackAndTraceScans | null;
  clientNo: string;              // default "" in C#
  useFallBack: boolean;          // default false in C#
}

export interface WayBillHeaders {
  wayBillNo: string;
  customerNo: string;
  date: string;                  // DateTimeOffset -> ISO string recommended
  accountNo: string;
  reference1: string;
  reference2: string;
  reference3: string;
  trackingNo: string;
  cashOnDelivery: number;        // decimal -> number
  deliveryTerms: string;
  terminalIn: string;
  terminalOut: string;
  remarks: string;
  francoText: string;
  pickupTime: string;            // DateTime -> ISO string recommended
  deliveryDateTime: string;      // DateTime -> ISO string recommended
  pickupType: string;
  deliveryType: string;
  serviceText: string;
  complaintText: string;
}

export interface AddreseBlock {
  name: string;
  street: string;
  street2: string;
  street3: string;
  zipCode: string;
  city: string;
  phone: string;
  country: string;               // default "DK" in C#
  contactPerson: string;
}

export interface ColliLines {
  quantity: number;
  colliType: string;
  colliDescription: string;
  weight: number;                // double -> number
  volumeM3: string;
  omexType: string;
}

export interface Totals {
  totalColli: number;
  totalWeightKg: number;
  totalVolumeM3: number;
  totalLdm: number;
}

export interface Omex {
  isOmex: boolean;
  omexCustomerNo: string;
  omexTerminalPickup: string;
  omexTerminalVia: string;
  omexTerminalDelivery: string;
  omexAccount: number;
  omexFinalAccount: string;
  omexTypeFlags: number;
}

export interface PalletExchange {
  enabled: boolean;
  pallet11: number;
  pallet12: number;
  pallet14: number;
}

export interface Service {
  serviceCodes: string;
  combinedServiceText: string;
  hasPalletExchange: boolean;
  hasCashOnDelivery: boolean;
  isOmex: boolean;
}

export interface TrackAndTraceScans {
  scans: any[]; // MessageContracts.V1.Shared.Scan.Scan -> define or import
}

//#endregion

